import { useMemo } from "react";

import type { DataPointSource } from "@/lib/api/building";

import { SignaalitList } from "./SignaalitList";
import { useBuildingSignaalitSnapshot } from "./useBuildingSignaalitSnapshot";
import type { VisibleSignalFilter } from "./visible-signal-filter";

interface Props {
  buildingId: string;
  /**
   * Optional resolver that narrows rendering to a subset of sources/signals while the
   * full building-wide `sources` set is still fetched (so connection-source history
   * resolution keeps scanning the whole set). Called once after sources load, and
   * whenever the callback reference changes (use `useCallback` at the call site to
   * avoid unnecessary re-evaluations). Presence also nests the list in the parent
   * panel: SignaalitList drops accordion chrome so signals are visible without a
   * click on a header that repeats the parent.
   */
  resolveVisibleSignals?: (sources: DataPointSource[]) => VisibleSignalFilter;
}

export function DataPointsExplorer({
  buildingId,
  resolveVisibleSignals,
}: Props) {
  const snapshot = useBuildingSignaalitSnapshot(buildingId);

  const visible_signals = useMemo(() => {
    if (snapshot.status !== "ok") return undefined;
    return resolveVisibleSignals?.(snapshot.bag.sources);
  }, [resolveVisibleSignals, snapshot]);

  if (snapshot.status === "loading") {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Ladataan signaaleja…
      </p>
    );
  }

  const { bag } = snapshot;

  return (
    <div className="space-y-6">
      <SignaalitList
        buildingId={buildingId}
        sources={bag.sources}
        samples={bag.samples}
        behavior_labels={bag.behavior_labels}
        mode="live"
        building_services={bag.building_services}
        context_setpoints_by_source={bag.context_setpoints_by_source}
        zone_equipment_signals={bag.zone_equipment_signals}
        zone_kpis={bag.zone_kpis}
        visible_signals={visible_signals}
        embedded={resolveVisibleSignals != null}
      />
    </div>
  );
}
